import { FC, useEffect, useMemo, useState } from "react";
import { Description, Label, Radio, RadioGroup } from "@headlessui/react";
import { useAppSelector } from "../../store";
import { capitalize } from "../../helpers/string/capitalize";
import { mergeClassNames } from "../../helpers";
import { getRoleDescription, parseRoleName } from "./helpers";
import { RoleTitle, RoleToDescription } from "./types";
import { isPermitted } from "../../helpers/permissions/featurePermission";
import { SMALL_ICON_SIZE_STYLES } from "../../ui/styles";

export type RoleSelectProps = {
  onChange: (arg: any) => void;
  roles: RoleTitle[];
};

export const RoleSelect: FC<React.PropsWithChildren<RoleSelectProps>> = ({
  onChange,
  roles,
}) => {
  const [role, setRole] = useState<RoleTitle>(
    roles.find((role) => !role.disabled)!,
  );
  const actualPermissions = useAppSelector(
    (state) => state.company.company?.featurePermissions,
  );
  const canEditOwner = useMemo(
    () =>
      actualPermissions &&
      isPermitted(actualPermissions, "company:owners:update"),
    [actualPermissions],
  );

  useEffect(() => {
    onChange(role);
  }, [role, onChange]);

  return (
    <RadioGroup value={role} onChange={setRole}>
      <Label className="sr-only">Member Roles</Label>

      <div className="relative -space-y-px rounded-md bg-stone-50 dark:bg-stone-900">
        {roles.map((role: RoleTitle, index: number) => {
          const roleName = parseRoleName(role);
          const disabled =
            role.disabled || (roleName === "company owner" && !canEditOwner);
          return (
            <Radio
              key={index}
              value={role}
              disabled={disabled}
              className={({ checked }) =>
                mergeClassNames(
                  index === 0 ? "rounded-tl-md rounded-tr-md" : "",
                  index === roles.length - 1
                    ? "rounded-bl-md rounded-br-md"
                    : "",
                  checked
                    ? "z-10 border-stone-200 bg-stone-50 dark:border-stone-700 dark:bg-stone-900"
                    : "border-stone-200 dark:border-stone-700",
                  "relative grid cursor-pointer grid-cols-2 flex-col border p-2 focus:outline-none md:px-4",
                  disabled ? "opacity-50" : "",
                )
              }
            >
              {({ focus, checked }) => (
                <>
                  <span className="flex items-center text-sm">
                    <span
                      className={mergeClassNames(
                        checked
                          ? "border-transparent bg-stone-600 dark:bg-stone-500"
                          : "border-stone-300 bg-stone-50 dark:border-stone-900",
                        focus ? "ring-2 ring-stone-500 ring-offset-2" : "",
                        `flex items-center justify-center rounded-full border ${SMALL_ICON_SIZE_STYLES}`,
                      )}
                      aria-hidden="true"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-stone-50" />
                    </span>
                    <Label
                      as="span"
                      className={mergeClassNames(
                        checked
                          ? "text-stone-900 dark:text-stone-100"
                          : "text-primary",
                        "ml-3 font-medium",
                      )}
                    >
                      {capitalize(roleName)}
                    </Label>
                  </span>

                  <Description
                    as="span"
                    className={mergeClassNames(
                      checked
                        ? "text-stone-700 dark:text-stone-400"
                        : "text-stone-500 dark:text-stone-400",
                      "ml-6 pl-1 text-sm md:ml-0 md:pl-0 md:text-right",
                    )}
                  >
                    {capitalize(
                      getRoleDescription(roleName as RoleToDescription),
                    )}
                  </Description>
                </>
              )}
            </Radio>
          );
        })}
      </div>
    </RadioGroup>
  );
};
